import type {
  ILookup,
  IndexedExternalLookups,
  IndexedInternalLookups,
} from '@/typings';

import { config } from '../config';
import type { FormField, FormInitialValues } from '../typings';
import {
  findFormField,
  isAddressGroupLive,
  resolveAddressOwner,
} from './addressLink';
import type { AddressGroupField, AddressOwner } from './addressLink';

export type AddressSummaryLookups = {
  internalLookups: IndexedInternalLookups;
  externalLookups: IndexedExternalLookups;
};

const usesShortName = (field: FormField | undefined): boolean =>
  Boolean(field && 'useShortName' in field && field.useShortName);

/** The internal lookup entry a stored `[#]type[#]id` part names; undefined for any other part. */
export const findInternalLookupEntry = (
  part: string,
  internalLookups: IndexedInternalLookups,
): ILookup | undefined => {
  if (!part.startsWith(config.internalLookupPrefix)) return undefined;

  const [, typeId, lookupId] = part.split(config.internalLookupPrefix);
  return internalLookups[Number(typeId)]?.find(
    lookup => lookup.id === lookupId,
  );
};

/** What the provider sees for one stored part of a field's value. */
const displayPart = (
  part: string,
  field: FormField | undefined,
  lookups: AddressSummaryLookups,
): string => {
  if (part.startsWith(config.internalLookupPrefix)) {
    const entry = findInternalLookupEntry(part, lookups.internalLookups);
    return (usesShortName(field) && entry?.shortName) || entry?.name || '';
  }

  if (part.startsWith(config.externalLookupPrefix)) {
    const id = part.slice(config.externalLookupPrefix.length);
    const entry = field
      ? lookups.externalLookups[field.fieldId]?.values.find(
          lookup => lookup.id === id,
        )
      : undefined;
    return (usesShortName(field) && entry?.shortName) || entry?.name || '';
  }

  return part.startsWith(config.customValuePrefix)
    ? part.slice(config.customValuePrefix.length)
    : part;
};

const displayValue = (
  raw: unknown,
  field: FormField | undefined,
  lookups: AddressSummaryLookups,
): string => {
  const isValue =
    typeof raw === 'string' || typeof raw === 'number' || Array.isArray(raw);
  if (!isValue) return '';

  const parts = Array.isArray(raw)
    ? raw.map(String)
    : String(raw).split(config.multiValueDivider);

  return parts
    .map(part => displayPart(part.trim(), field, lookups).trim())
    .filter(text => text !== '')
    .join(' ');
};

/** What the provider sees in one of a group's fields, e.g. a picked street by its name. */
export const describeAddressField = (
  group: AddressGroupField,
  fieldId: string,
  values: FormInitialValues,
  lookups: AddressSummaryLookups,
): string =>
  displayValue(values[fieldId], findFormField(group.fields, fieldId), lookups);

/** One line built from a group's address fields, e.g. "200 Main St, Denver, CO". */
export const summarizeAddress = (
  group: AddressGroupField,
  values: FormInitialValues,
  lookups: AddressSummaryLookups,
): string =>
  group.address.summaryFieldIds
    .map(fieldId => describeAddressField(group, fieldId, values, lookups))
    .filter(text => text !== '')
    .join(', ');

/** The address line of a resolved owner; empty while there is none. */
export const summarizeOwner = (
  owner: AddressOwner,
  values: FormInitialValues,
  lookups: AddressSummaryLookups,
): string => {
  switch (owner.kind) {
    case 'group':
      return summarizeAddress(owner.group, values, lookups);
    case 'server':
      return owner.summary ?? '';
    case 'none':
      return '';
  }
};

/** A target group's own name and the address line of the group that holds its address; both empty while the target isn't live. */
export const describeAddressTarget = (
  targetFieldId: string,
  index: ReadonlyMap<string, AddressGroupField>,
  values: FormInitialValues,
  lookups: AddressSummaryLookups,
): { name: string; address: string } => {
  const target = index.get(targetFieldId);
  if (!target || !isAddressGroupLive(target, values)) {
    return { name: '', address: '' };
  }

  const nameFieldId = target.address.nameFieldId;

  return {
    name: nameFieldId
      ? displayValue(
          values[nameFieldId],
          findFormField(target.fields, nameFieldId),
          lookups,
        )
      : '',
    address: summarizeOwner(
      resolveAddressOwner(targetFieldId, index, values),
      values,
      lookups,
    ),
  };
};
